import { findLastIndex } from 'ramda';
import { padArrayEnd } from '../../../Utils/utils';
import {
  IBusinessConfigDefaultSortInfo,
  tryFindDefaultSort,
} from '../_Common/Const/BusinessConfigs';
import { IColumnDef } from '../../../Types/ColumnDefType';
import { ISortProperty } from '../../../BaseComponents/Table/Components/SortableTableUtils';
import Logger from '../../../Utils/Logger';
import { getColumnWithCaption } from '../../../Components/KolumnValjare/Components/KolumnValjareDialog/KolumnValjareDialogUtils';
import { IAvenyModelContext } from '../../../Model/ModelContext/ModelContext';

export interface INavLevelEntry {
  count: number;
  breadcrumbCount: number;
}

export interface INavLevelAction {
  type: 'reg' | 'unreg';
  level: number;
  showInBreadcrumb?: boolean;
}

/** Uppdaterar en lista över vilka navigeringsnivåer som används.
 * På ett visst index i listan registreras antalet komponenter som registrerat att de renderar en header på den nivån.
 * Det borde bara vara 0 eller 1 header per nivå, men vi har antal istället för boolean för att inte vara beroende på timingen av avregistrering och registreringar.
 * showInBreadcrumb (default true) styr om nivån räknas som synlig i breadcrumb-titeln (t.ex. false vid showDetailsRight).
 */
export const navlevelsReducer = (
  state: INavLevelEntry[],
  action: INavLevelAction
): INavLevelEntry[] => {
  const showInBreadcrumb = action.showInBreadcrumb ?? true;
  const padded = padArrayEnd(
    { count: 0, breadcrumbCount: 0 },
    action.level + 1,
    state
  );
  const entry = padded[action.level];
  const updated = [...padded];
  switch (action.type) {
    case 'reg':
      updated[action.level] = {
        count: entry.count + 1,
        breadcrumbCount: entry.breadcrumbCount + (showInBreadcrumb ? 1 : 0),
      };
      return updated;
    case 'unreg':
      updated[action.level] = {
        count: entry.count - 1,
        breadcrumbCount: entry.breadcrumbCount - (showInBreadcrumb ? 1 : 0),
      };
      return updated;
  }
};

export const topNavLevel = (state: INavLevelEntry[]): number =>
  findLastIndex((e: INavLevelEntry) => e.count > 0, state);

export const topBreadcrumbNavLevel = (state: INavLevelEntry[]): number =>
  findLastIndex((e: INavLevelEntry) => e.breadcrumbCount > 0, state);

export const getSortPropertiesFromConfig = (
  businessConfigDefaultSortInfoList: IBusinessConfigDefaultSortInfo[],
  columns: readonly IColumnDef[],
  collectionName: string,
  modelContext: IAvenyModelContext
) => {
  return businessConfigDefaultSortInfoList.reduce<ISortProperty[]>(
    (prevInitialProperties, defaultSort) => {
      const columnDef = columns.find(
        column => column.attributeDef === defaultSort.attributeName
      );

      if (!columnDef) {
        Logger.error(
          new Error(
            `Erro while setting default sort for collection ${collectionName}. Tried to sort on attribute ${defaultSort.attributeName} which is not added to columns`
          )
        );
      }

      if (columnDef) {
        prevInitialProperties.push({
          caption:
            getColumnWithCaption(collectionName, modelContext, columnDef)
              .caption || '',
          orderBy: columnDef.attributeDef,
          order: defaultSort.order,
        });
      }

      return prevInitialProperties;
    },
    []
  );
};

export const getSortPropertiesFromColumns = (
  columns: readonly IColumnDef[],
  collectionName: string,
  modelContext: IAvenyModelContext
) => {
  return columns.reduce<ISortProperty[]>((a, b) => {
    if (b.header && b.attributeDef) {
      a.push({
        caption:
          getColumnWithCaption(collectionName, modelContext, b).caption || '',
        orderBy: b.attributeDef,
        order: 'asc',
      });
    }
    return a;
  }, []);
};

export const getDefaultSortProperties = (
  columns: readonly IColumnDef[],
  collectionName: string,
  modelContext: IAvenyModelContext,
  rutinKod: string
): ISortProperty[] => {
  let initialProperties: ISortProperty[] = [];

  const defaultSort = tryFindDefaultSort(collectionName, rutinKod);

  if (defaultSort) {
    initialProperties = getSortPropertiesFromConfig(
      defaultSort,
      columns,
      collectionName,
      modelContext
    );
  } else {
    initialProperties = getSortPropertiesFromColumns(
      columns,
      collectionName,
      modelContext
    );
  }

  return initialProperties;
};
